import { cn } from '@/lib/utils';
import type { ActionBarConfig } from '@/lib/types/action-bar.types';
import { Button } from '@/components/ui/button';
import {
  CheckIcon,
  Circle,
} from '@phosphor-icons/react';

type ActionBarProps = {
  config: ActionBarConfig;
};

/**
 * ActionBar Component
 *
 * A reusable bottom action bar that appears above the navigation bar.
 * This component is rendered by the Navigation component and controlled via
 * the ActionBarContext. Pages should use the `useActionBar` hook to register
 * their action buttons rather than rendering this component directly.
 *
 * @example
 * ```tsx
 * // In your page component:
 * import { useActionBar } from '@/lib/context/ActionBarContext';
 *
 * function MyPage() {
 *   useActionBar({
 *     title: 'Review & Save',
 *     subtext: 'This is subtext',
 *     visible: true,
 *     actions: [
 *       {
 *         key: 'cancel',
 *         label: 'Cancel',
 *         variant: 'outline',
 *         fullWidth: true,
 *         onClick: () => handleCancel(),
 *       },
 *       {
 *         key: 'save',
 *         label: 'Save',
 *         variant: 'default',
 *         fullWidth: true,
 *         loading: isSubmitting,
 *         onClick: () => handleSave(),
 *       },
 *     ],
 *   });
 *
 *   return <div>Page content</div>;
 * }
 * ```
 *
 * @remarks
 * - The action bar automatically cleans up when the component unmounts
 * - Actions support loading states (shows "Loading..." text)
 * - Buttons can be disabled individually or when loading
 * - Use `fullWidth: true` for equal-width buttons in a row
 * - The action bar is positioned above the navigation bar with proper spacing
 *
 * @see {@link useActionBar} - Hook to register action bar from page components
 * @see {@link ActionBarContext} - Context provider that manages action bar state
 *
 * @param props - Component props
 * @param props.config - Configuration object containing title, visibility, and actions
 * @param props.config.title - Optional title displayed above the action buttons
 * @param props.config.subtext - Optional subtext displayed below the title
 * @param props.config.visible - Whether the action bar should be visible (defaults to true)
 * @param props.config.actions - Array of action button configurations
 * @param props.config.actions[].key - Unique key for the action button
 * @param props.config.actions[].label - Button label text
 * @param props.config.actions[].onClick - Click handler function
 * @param props.config.actions[].variant - Button variant ('default' | 'outline' | 'ghost' | 'secondary' | 'destructive' | 'link')
 * @param props.config.actions[].disabled - Whether the button is disabled
 * @param props.config.actions[].loading - Whether the button is in a loading state
 * @param props.config.actions[].fullWidth - Whether the button should take full width
 * @param props.config.className - Optional additional CSS classes for the container
 *
 * @returns The rendered action bar or null if not visible or no actions
 */
export function ActionBar({ config }: ActionBarProps) {
  if (!config?.actions?.length || config.visible === false) return null;

  const buttonStyle = config.buttonStyle;

  const renderButton = (
    action: ActionBarConfig['actions'][0],
    index: number,
    total: number
  ) => {
    const {
      key,
      label,
      onClick,
      variant = 'default',
      disabled,
      loading,
      fullWidth,
      showCheckmark,
    } = action;

    const isSelected = showCheckmark;
    const isFirst = index === 0;
    const isLast = index === total - 1;

    // Original style (no special variations) - used when buttonStyle is undefined
    if (!buttonStyle) {
      return (
        <Button
          key={key}
          type="button"
          variant={variant}
          onClick={onClick}
          disabled={disabled || loading}
          className={cn(fullWidth ? 'w-full' : '', 'h-16')}
        >
          {loading ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {showCheckmark && <CheckIcon size={20} weight="fill" />}
              {label}
            </span>
          )}
        </Button>
      );
    }

    // Variation 1: Segmented Control Style
    if (buttonStyle === 'segmented') {
      return (
        <Button
          key={key}
          type="button"
          variant={isSelected ? 'default' : 'outline'}
          onClick={onClick}
          disabled={disabled || loading}
          className={cn(
            fullWidth ? 'w-full' : '',
            'h-16 relative',
            // Remove border radius on inner edges for connected look
            !isFirst && 'rounded-l-none -ml-[1px]',
            !isLast && 'rounded-r-none',
            isSelected && 'z-10 shadow-sm'
          )}
        >
          {loading ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isSelected && <CheckIcon size={20} weight="fill" />}
              {label}
            </span>
          )}
        </Button>
      );
    }

    // Variation 2: Card-Style with Border Emphasis
    if (buttonStyle === 'card') {
      return (
        <Button
          key={key}
          type="button"
          variant={isSelected ? 'default' : 'outline'}
          onClick={onClick}
          disabled={disabled || loading}
          className={cn(
            fullWidth ? 'w-full' : '',
            'h-16',
            isSelected
              ? 'border-2 border-green-500 ring-2 ring-green-500/20 shadow-md'
              : 'bg-slate-700/50 border border-slate-500'
          )}
        >
          {loading ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isSelected && <CheckIcon size={20} weight="fill" />}
              {label}
            </span>
          )}
        </Button>
      );
    }

    // Variation 3: Minimal with Icon Emphasis
    if (buttonStyle === 'minimal') {
      return (
        <Button
          key={key}
          type="button"
          variant={isSelected ? 'default' : 'secondary'}
          onClick={onClick}
          disabled={disabled || loading}
          className={cn(
            fullWidth ? 'w-full' : '',
            'h-16',
            isSelected && 'font-bold'
          )}
        >
          {loading ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isSelected ? (
                <CheckIcon size={24} weight="fill" />
              ) : (
                <Circle size={20} weight="regular" className="opacity-50" />
              )}
              <span className={isSelected ? 'font-semibold' : ''}>{label}</span>
            </span>
          )}
        </Button>
      );
    }

    // Variation 4: Elevated Selected State
    if (buttonStyle === 'elevated') {
      return (
        <Button
          key={key}
          type="button"
          variant={isSelected ? 'default' : 'outline'}
          onClick={onClick}
          disabled={disabled || loading}
          className={cn(
            fullWidth ? 'w-full' : '',
            'h-16 transition-all',
            isSelected
              ? 'shadow-lg ring-2 ring-green-500/30 scale-[1.02]'
              : 'bg-slate-700/30 border-slate-500'
          )}
        >
          {loading ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isSelected && <CheckIcon size={20} weight="fill" />}
              {label}
            </span>
          )}
        </Button>
      );
    }

    // Default fallback (shouldn't reach here)
    return (
      <Button
        key={key}
        type="button"
        variant={variant}
        onClick={onClick}
        disabled={disabled || loading}
        className={cn(fullWidth ? 'w-full' : '', 'h-16')}
      >
        {loading ? (
          'Loading...'
        ) : (
          <span className="flex items-center gap-2">
            {showCheckmark && <CheckIcon size={20} weight="fill" />}
            {label}
          </span>
        )}
      </Button>
    );
  };

  return (
    <div className={cn('bg-background p-4', config.className)}>
      {config.title && <h3>{config.title}</h3>}
      <div
        className={cn(
          'mx-auto flex w-full max-w-4xl items-center py-3',
          buttonStyle === 'segmented' ? 'gap-0' : 'gap-3'
        )}
      >
        {config.actions.map((action, index) =>
          renderButton(action, index, config.actions.length)
        )}
      </div>
      {config.subtext && (
        <p className="text-small text-slate-400">{config.subtext}</p>
      )}
    </div>
  );
}

